import React, { ReactNode, createContext, useMemo, useState } from "react";
import styled from "styled-components";
import { PortalFunctionParams } from "react-portal";
import { useTrigger } from "@sonder/ui/source/hooks/useTrigger";
import { useWindowSize } from "@sonder/ui/source/hooks/useWindowSize";
import { zIndex } from "@sonder/ui/source/styled/theme";

export interface PortalRenderFn {
  (props: PortalFunctionParams): ReactNode;
}

interface StyledPortalProps {
  minWidth: number;
}

interface PortalWithTriggerProps extends PortalFunctionParams {
  className?: string;
  children: ReactNode;
  trigger: JSX.Element;
}

export const StyledPortal = styled.div<StyledPortalProps>`
  left: 0;
  min-width: ${props => props.minWidth}px;
  position: fixed;
  top: 0;
  z-index: ${zIndex.modal};
`;

const initialBoundingContext = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
};
export const BoundingRectContext = createContext(initialBoundingContext);

export const PortalWithTrigger = ({
  className,
  children,
  closePortal,
  isOpen,
  openPortal,
  portal,
  trigger,
}: PortalWithTriggerProps) => {
  const size = useWindowSize({ debounceInterval: 16 });
  const [triggerElement, setTriggerElement] = useState<
    (EventTarget & Element) | null
  >(null);
  const Trigger = useTrigger(trigger, e => {
    setTriggerElement(e.currentTarget);
    // See: https://github.com/tajo/react-portal/issues/175
    requestAnimationFrame(() => (isOpen ? closePortal() : openPortal()));
  });
  const rect = useMemo(
    () =>
      (triggerElement && triggerElement.getBoundingClientRect()) ||
      initialBoundingContext,
    [triggerElement, isOpen, size.width, size.height],
  );
  return (
    <>
      {Trigger}
      {portal(
        <StyledPortal
          className={className}
          minWidth={Math.round(rect.width)}
          onClick={e => e.stopPropagation()}
        >
          <BoundingRectContext.Provider value={rect}>
            {children}
          </BoundingRectContext.Provider>
        </StyledPortal>,
      )}
    </>
  );
};
